import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { AUTH_STORAGE_KEY, RIPIK_INTERNAL_DOMAIN } from '@/shared/config/constants';

/**
 * Auth store.
 *
 * Holds JWT, current user, and deployment role assignments. Persisted to
 * localStorage via Zustand persist middleware. Axios interceptor reads
 * token via getState() — no React subscription.
 *
 * SECURITY NOTE: persisting the JWT in localStorage means any XSS payload can
 * read/exfiltrate it. This is the standard SPA tradeoff and is accepted for now
 * because the only innerHTML sink in the app (overviewDiagram SVG) is
 * DOMPurify-sanitized. Migration target: drop persist() and move the token to
 * an httpOnly cookie, hydrating user/assignments from GET /me, once the BE
 * supports cookie auth. Do not add new innerHTML/dangerouslySetInnerHTML sinks
 * while the token lives in localStorage.
 */

function isRipikEmail(email) {
  return typeof email === 'string' && email.toLowerCase().endsWith(RIPIK_INTERNAL_DOMAIN);
}

/**
 * Normalizes the `assignments` field from a login response. `assignments` is the
 * authorization payload; a missing/non-array value means the response is
 * malformed (not "user has no deployments"). We surface that loudly rather than
 * silently treating it as a valid zero-deployment user, but still fall back to
 * an empty array so the app does not crash.
 */
function normalizeAssignments(assignments) {
  if (Array.isArray(assignments)) return assignments;
  console.error(
    '[authStore] login response missing a valid `assignments` array; defaulting to []. ' +
      'This usually means the auth response shape is wrong.',
    assignments
  );
  return [];
}

export const useAuthStore = create(
  persist(
    (set) => ({
      token: null,
      user: null,
      assignments: [],
      isInternalRipik: false,

      setAuth: ({ token, user, assignments }) =>
        set({
          token,
          user,
          assignments: normalizeAssignments(assignments),
          isInternalRipik: isRipikEmail(user?.email),
        }),

      clearAuth: () =>
        set({ token: null, user: null, assignments: [], isInternalRipik: false }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({
        token: s.token,
        user: s.user,
        assignments: s.assignments,
        isInternalRipik: s.isInternalRipik,
      }),
    }
  )
);
